import { useCallback, useEffect, useRef, useState } from 'react';
import {
  eventServiceApi,
  useCancelEventMutation,
  useDeleteDraftEventMutation,
} from '@/store/api/eventService';
import type { GetEventsParams as ApiGetEventsParams } from '@/store/models';
import type {
  BookingsPaginationSession,
  BookingsTabPageCache,
  DashboardTab,
  EventSummary,
  GetEventsParams,
  PaginatedResponse,
} from '@/types/events.types';
import {
  getBookingTabStatusFilter,
  groupEventsBySection,
  normalizeEventState,
  STATE_TO_SECTION,
} from '@/types/events.types';
import { useAppDispatch } from '@/store/hooks';
import type { BuildingId, EventId } from '@/store/models/ids';

const PAGE_SIZE = 10 as const;

const TAB_QUERY_CONFIGS: Record<DashboardTab, GetEventsParams> = {
  current: {
    tab: 'active',
    status: getBookingTabStatusFilter('current'),
    pageSize: PAGE_SIZE,
    sortBy: 'eventStartDate',
    sortOrder: 'asc',
  },
  past: {
    status: getBookingTabStatusFilter('past'),
    pageSize: PAGE_SIZE,
    sortBy: 'eventStartDate',
    sortOrder: 'desc',
  },
  draft: {
    status: getBookingTabStatusFilter('draft'),
    pageSize: PAGE_SIZE,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
};

function createEmptyTabState(): BookingsTabPageCache {
  return {
    currentPage: 1,
    totalCount: null,
    pageCount: 0,
    pages: {},
    tokensByPageStart: { 1: null },
    lastKnownContinuationToken: null,
    hasMore: false,
    loading: false,
    error: null,
  };
}

function createLoadingTabState(): BookingsTabPageCache {
  return {
    ...createEmptyTabState(),
    loading: true,
  };
}

function createInitialSession(): BookingsPaginationSession {
  return {
    activeTab: 'current',
    defaultTab: 'current',
    tabs: {
      current: createEmptyTabState(),
      past: createEmptyTabState(),
      draft: createEmptyTabState(),
    },
    prefetchStatus: {
      current: 'idle',
      past: 'idle',
      draft: 'idle',
    },
  };
}

function getPageCount(totalCount: number | null): number {
  if (!totalCount || totalCount <= 0) {
    return 0;
  }

  return Math.ceil(totalCount / PAGE_SIZE);
}

function toErrorMessage(
  error: unknown,
  fallback = 'Unable to load events. Please try again.'
): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'object' && error && 'message' in error) {
    return String((error as { message?: string }).message ?? fallback);
  }

  return fallback;
}

function getTabEvents(
  tab: DashboardTab,
  response: PaginatedResponse<EventSummary>
): EventSummary[] {
  if (!response.success) {
    return [];
  }

  return groupEventsBySection(response.data)[tab];
}

function isSuccessfulPaginatedResponse(
  response: PaginatedResponse<EventSummary>
): response is Extract<PaginatedResponse<EventSummary>, { success: true }> {
  return response.success;
}

export interface UseBookingsPaginatedEventsReturn {
  tabs: Record<DashboardTab, BookingsTabPageCache>;
  prefetchStatus: BookingsPaginationSession['prefetchStatus'];
  isInitialLoadComplete: boolean;
  setPage: (tab: DashboardTab, page: number) => Promise<void>;
  retryTab: (tab: DashboardTab) => Promise<void>;
  refetch: () => Promise<void>;
  getEventForAction: (eventId: string) => Promise<EventSummary>;
  deleteEvent: (eventId: string, buildingId: string) => Promise<void>;
  cancelEvent: (eventId: string, buildingId: string, comments?: string) => Promise<void>;
}

function updateEventStateInPages(
  pages: Record<number, EventSummary[]>,
  eventId: string,
  nextState: EventSummary['eventState'],
  cancelledReason?: string
): Record<number, EventSummary[]> {
  let hasUpdated = false;

  const nextPages = Object.fromEntries(
    Object.entries(pages).map(([page, events]) => {
      const nextEvents = events.map((event) => {
        if (event.eventId !== eventId) {
          return event;
        }

        hasUpdated = true;

        return {
          ...event,
          eventState: nextState,
          ...(cancelledReason !== undefined ? { cancelledReason } : {}),
        };
      });

      return [Number(page), nextEvents];
    })
  ) as Record<number, EventSummary[]>;

  return hasUpdated ? nextPages : pages;
}

type LoadedPageResult = {
  resolvedPage: number;
  totalCount: number | null;
  pageCount: number;
  nextContinuationToken: string | null;
};

export function useBookingsPaginatedEvents(): UseBookingsPaginatedEventsReturn {
  const dispatch = useAppDispatch();
  const [deleteDraft] = useDeleteDraftEventMutation();
  const [cancelMutation] = useCancelEventMutation();
  const [session, setSession] = useState<BookingsPaginationSession>(createInitialSession);
  const sessionRef = useRef(session);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const updateTabState = useCallback(
    (tab: DashboardTab, updater: (state: BookingsTabPageCache) => BookingsTabPageCache) => {
      setSession((prev) => {
        const next = {
          ...prev,
          tabs: {
            ...prev.tabs,
            [tab]: updater(prev.tabs[tab]),
          },
        };

        sessionRef.current = next;

        return next;
      });
    },
    []
  );

  const updatePrefetchStatus = useCallback(
    (tab: DashboardTab, status: BookingsPaginationSession['prefetchStatus'][DashboardTab]) => {
      setSession((prev) => {
        const next = {
          ...prev,
          prefetchStatus: {
            ...prev.prefetchStatus,
            [tab]: status,
          },
        };

        sessionRef.current = next;

        return next;
      });
    },
    []
  );

  const executeGetEvents = useCallback(
    async (params: GetEventsParams) => {
      const request = dispatch(
        eventServiceApi.endpoints.getEvents.initiate(params as ApiGetEventsParams)
      );

      try {
        return await request.unwrap();
      } finally {
        request.unsubscribe?.();
      }
    },
    [dispatch]
  );

  const loadSinglePage = useCallback(
    async (
      tab: DashboardTab,
      page: number,
      force = false,
      totalCountOverride?: number | null,
      tokenOverride?: string | null
    ): Promise<LoadedPageResult> => {
      const currentState = sessionRef.current.tabs[tab];

      if (!force && currentState.pages[page]) {
        updateTabState(tab, (state) => ({
          ...state,
          currentPage: page,
          error: null,
        }));
        return {
          resolvedPage: page,
          totalCount: currentState.totalCount,
          pageCount: currentState.pageCount,
          nextContinuationToken: currentState.tokensByPageStart[page + 1] ?? null,
        } satisfies LoadedPageResult;
      }

      let resolvedToken = tokenOverride ?? null;

      if (page > 1) {
        for (let candidatePage = 1; candidatePage < page; candidatePage += 1) {
          const candidateState = sessionRef.current.tabs[tab];

          if (!candidateState.pages[candidatePage]) {
            const candidateResult = await loadSinglePage(tab, candidatePage, force);
            if (candidatePage === page - 1) {
              resolvedToken = candidateResult.nextContinuationToken;
            }

            if (
              !candidateResult.nextContinuationToken &&
              candidateResult.pageCount <= candidatePage
            ) {
              const clampedPage = Math.max(1, candidateResult.pageCount || 1);
              updateTabState(tab, (state) => ({
                ...state,
                currentPage: Math.min(page, clampedPage),
              }));
              return {
                resolvedPage: Math.min(page, clampedPage),
                totalCount: candidateResult.totalCount,
                pageCount: candidateResult.pageCount,
                nextContinuationToken: candidateResult.nextContinuationToken,
              } satisfies LoadedPageResult;
            }

            continue;
          }

          const refreshedState = sessionRef.current.tabs[tab];
          if (candidatePage === page - 1) {
            resolvedToken = refreshedState.tokensByPageStart[candidatePage + 1] ?? null;
          }

          if (!refreshedState.tokensByPageStart[candidatePage + 1] && !refreshedState.hasMore) {
            const clampedPage = Math.max(1, refreshedState.pageCount || 1);
            updateTabState(tab, (state) => ({
              ...state,
              currentPage: Math.min(page, clampedPage),
            }));
            return {
              resolvedPage: Math.min(page, clampedPage),
              totalCount: refreshedState.totalCount,
              pageCount: refreshedState.pageCount,
              nextContinuationToken: refreshedState.tokensByPageStart[candidatePage + 1] ?? null,
            } satisfies LoadedPageResult;
          }
        }
      }

      const token =
        page === 1
          ? null
          : (tokenOverride ??
            resolvedToken ??
            sessionRef.current.tabs[tab].tokensByPageStart[page] ??
            null);

      if (page > 1 && token === null) {
        const fallbackPage =
          Object.keys(sessionRef.current.tabs[tab].pages)
            .map(Number)
            .filter((loadedPage) => loadedPage > 0)
            .sort((left, right) => right - left)[0] ?? 1;

        updateTabState(tab, (state) => ({
          ...state,
          currentPage: Math.min(page, fallbackPage),
        }));
        return {
          resolvedPage: Math.min(page, fallbackPage),
          totalCount: sessionRef.current.tabs[tab].totalCount,
          pageCount: sessionRef.current.tabs[tab].pageCount,
          nextContinuationToken: sessionRef.current.tabs[tab].tokensByPageStart[page + 1] ?? null,
        } satisfies LoadedPageResult;
      }

      updateTabState(tab, (state) => ({
        ...state,
        loading: true,
        error: null,
      }));

      try {
        const response = (await executeGetEvents({
          ...TAB_QUERY_CONFIGS[tab],
          continuationToken: token ?? undefined,
        })) as PaginatedResponse<EventSummary>;

        if (!isSuccessfulPaginatedResponse(response)) {
          throw new Error(response.error.message);
        }

        const tabEvents = getTabEvents(tab, response);
        // groupEventsBySection may move events to a different tab (e.g.
        // future-dated CANCELLED events move from past → current). Adjust
        // totalCount so the badge and pagination reflect what is actually
        // displayed, not the raw API count.
        const movedOutCount = response.data.length - tabEvents.length;
        const previousState = sessionRef.current.tabs[tab];
        const rawTotalCount =
          page === 1
            ? response.pagination.totalCount
            : (totalCountOverride ?? previousState.totalCount);
        const totalCount = Math.max(0, (rawTotalCount ?? 0) - movedOutCount);
        const pageCount = getPageCount(totalCount);
        const nextContinuationToken = response.pagination.continuationToken ?? null;

        updateTabState(tab, (state) => ({
          ...state,
          currentPage: Math.min(page, Math.max(1, pageCount || 1)),
          totalCount,
          pageCount,
          pages: {
            ...(page === 1 && force ? {} : state.pages),
            [page]: tabEvents,
          },
          tokensByPageStart: {
            ...(page === 1 && force ? { 1: null } : state.tokensByPageStart),
            [page]: token,
            ...(nextContinuationToken ? { [page + 1]: nextContinuationToken } : {}),
          },
          lastKnownContinuationToken: nextContinuationToken,
          hasMore: response.pagination.hasMore,
          loading: false,
          error: null,
        }));

        updatePrefetchStatus(tab, 'ready');

        return {
          resolvedPage: Math.min(page, Math.max(1, pageCount || 1)),
          totalCount,
          pageCount,
          nextContinuationToken,
        } satisfies LoadedPageResult;
      } catch (error) {
        updateTabState(tab, (state) => ({
          ...state,
          loading: false,
          error: toErrorMessage(error),
        }));
        updatePrefetchStatus(tab, 'error');

        return {
          resolvedPage: sessionRef.current.tabs[tab].currentPage,
          totalCount: sessionRef.current.tabs[tab].totalCount,
          pageCount: sessionRef.current.tabs[tab].pageCount,
          nextContinuationToken: sessionRef.current.tabs[tab].tokensByPageStart[page + 1] ?? null,
        } satisfies LoadedPageResult;
      }
    },
    [executeGetEvents, updatePrefetchStatus, updateTabState]
  );

  const setPage = useCallback(
    async (tab: DashboardTab, page: number) => {
      await loadSinglePage(tab, Math.max(1, page));
    },
    [loadSinglePage]
  );

  const retryTab = useCallback(
    async (tab: DashboardTab) => {
      updatePrefetchStatus(tab, 'loading');
      updateTabState(tab, () => createLoadingTabState());
      await loadSinglePage(tab, 1, true);
    },
    [loadSinglePage, updatePrefetchStatus, updateTabState]
  );

  const refetchTab = useCallback(
    async (tab: DashboardTab) => {
      const previousPage = sessionRef.current.tabs[tab].currentPage;
      updatePrefetchStatus(tab, 'loading');
      updateTabState(tab, () => createLoadingTabState());
      const refreshedFirstPage = await loadSinglePage(tab, 1, true);
      const nextPageCount = refreshedFirstPage?.pageCount ?? 0;

      const targetPage = Math.min(previousPage, Math.max(1, nextPageCount || 1));
      let resolvedPage = refreshedFirstPage?.resolvedPage ?? 1;

      let continuationToken = refreshedFirstPage?.nextContinuationToken ?? null;

      for (let nextPage = 2; nextPage <= targetPage; nextPage += 1) {
        const refreshedPage = await loadSinglePage(
          tab,
          nextPage,
          true,
          refreshedFirstPage?.totalCount,
          continuationToken
        );

        resolvedPage = refreshedPage?.resolvedPage ?? resolvedPage;
        continuationToken = refreshedPage?.nextContinuationToken ?? null;
      }

      updateTabState(tab, (state) => ({
        ...state,
        currentPage: resolvedPage,
        pageCount: nextPageCount,
      }));
    },
    [loadSinglePage, updatePrefetchStatus, updateTabState]
  );

  const refetch = useCallback(async () => {
    await Promise.all([refetchTab('current'), refetchTab('past'), refetchTab('draft')]);
  }, [refetchTab]);

  const deleteEvent = useCallback(
    async (eventId: string, buildingId: string) => {
      await deleteDraft({
        eventId: eventId as EventId,
        buildingId: buildingId as BuildingId,
      }).unwrap();

      await refetchTab('draft');
    },
    [deleteDraft, refetchTab]
  );

  const getEventForAction = useCallback(
    async (eventId: string) => {
      const detailRequest = dispatch(
        eventServiceApi.endpoints.getEventById.initiate({
          eventId: eventId as EventId,
        })
      );

      try {
        const detail = await detailRequest.unwrap();
        if (!detail.success) {
          const detailError = (detail as { error?: unknown }).error;
          throw new Error(toErrorMessage(detailError, 'Unable to load event. Please try again.'));
        }

        return detail.data as EventSummary;
      } finally {
        detailRequest.unsubscribe?.();
      }
    },
    [dispatch]
  );

  const cancelEvent = useCallback(
    async (eventId: string, buildingId: string, comments = '') => {
      const detailRequest = dispatch(
        eventServiceApi.endpoints.getEventById.initiate({
          eventId: eventId as EventId,
          buildingId: buildingId as BuildingId,
        })
      );

      try {
        const detail = await detailRequest.unwrap();

        // Unsubscribe before the mutation so its tag invalidation
        // doesn't trigger a refetch that immediately gets aborted.
        detailRequest.unsubscribe?.();

        const cancelResult = await cancelMutation({
          eventId: eventId as EventId,
          buildingId: buildingId as BuildingId,
          body: { action: 'SUBMITTED_FOR_CANCELLATION', comments },
          etag: detail.etag,
        }).unwrap();

        if (cancelResult.success) {
          const nextState = normalizeEventState(cancelResult.data.newState);
          const eventEndDate = detail.data?.eventEndDate
            ? new Date(detail.data.eventEndDate)
            : null;
          const isFutureEvent = !eventEndDate || eventEndDate >= new Date();

          // Tab placement is date-driven: future-dated events stay in current
          // regardless of their new state (e.g. CANCELLED, DECLINED).
          if (isFutureEvent || STATE_TO_SECTION[nextState] === 'current') {
            updateTabState('current', (state) => ({
              ...state,
              pages: updateEventStateInPages(state.pages, eventId, nextState, comments),
            }));
            return;
          }
        }
      } catch (error: unknown) {
        detailRequest.unsubscribe?.();
        void Promise.all([refetchTab('current'), refetchTab('past')]);
        throw error;
      }

      void Promise.all([refetchTab('current'), refetchTab('past')]);
    },
    [cancelMutation, dispatch, refetchTab, updateTabState]
  );

  useEffect(() => {
    let disposed = false;

    const initialize = async () => {
      updatePrefetchStatus('current', 'loading');
      await loadSinglePage('current', 1, true);

      if (disposed) {
        return;
      }

      updatePrefetchStatus('past', 'loading');
      await loadSinglePage('past', 1, true);

      if (disposed) {
        return;
      }

      updatePrefetchStatus('draft', 'loading');
      await loadSinglePage('draft', 1, true);
    };

    void initialize();

    return () => {
      disposed = true;
    };
  }, [loadSinglePage, updatePrefetchStatus]);

  const isInitialLoadComplete = (Object.keys(session.prefetchStatus) as DashboardTab[]).every(
    (tab) => session.prefetchStatus[tab] === 'ready' || Boolean(session.tabs[tab].error)
  );

  return {
    tabs: session.tabs,
    prefetchStatus: session.prefetchStatus,
    isInitialLoadComplete,
    setPage,
    retryTab,
    refetch,
    getEventForAction,
    deleteEvent,
    cancelEvent,
  };
}
